import React, { useState } from 'react';
import { Check } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAppData } from '../context/AppDataContext';
import NumberInput from './NumberInput';
import { getRemainingPages } from '../utils/bookUtils';
import { getPageScale } from '../utils/pageScale';

/**
 * 오늘 읽은 쪽수를 그 자리에서 바로 기록한다.
 * 목록·상세 헤더 어디에 놓아도 되도록 작게 유지한다. Enter 로 저장.
 */
const ProgressQuickInput = ({ book, onValueChange, onSaved, autoFocus = false }) => {
  const { recordProgress } = useAppData();
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  const scale = getPageScale(book);
  // 남은 쪽수는 사용자가 보는 단위로 비교해야 안내가 어긋나지 않는다
  const remaining = scale.toDisplay(getRemainingPages(book)) ?? 0;
  const pages = parseInt(value, 10);
  const valid = Number.isFinite(pages) && pages > 0;

  const handleChange = (next) => {
    setValue(next);
    if (onValueChange) {
      const n = parseInt(next, 10);
      onValueChange(Number.isFinite(n) && n > 0 ? n : null);
    }
  };

  const handleSave = async () => {
    if (!valid || saving) return;
    if (pages > remaining) {
      toast.error(`남은 쪽수(${remaining}쪽)를 초과할 수 없어요`);
      return;
    }

    setSaving(true);
    try {
      // 저장은 항상 기준 쪽수로
      await recordProgress(book.id, scale.toCanonical(pages));
      toast.success(`${book.title} +${pages}쪽`);
      handleChange('');
      if (onSaved) onSaved(pages);
    } catch (error) {
      toast.error(error.message || '기록에 실패했어요');
    } finally {
      setSaving(false);
    }
  };

  if (remaining === 0) {
    return (
      <span className="text-[11px] text-ink-500 dark:text-ink-400 font-sans shrink-0">
        다 읽었어요
      </span>
    );
  }

  return (
    <div className="flex items-center gap-1.5 shrink-0">
      <div className="relative">
        <NumberInput
          min="1"
          max={remaining}
          value={value}
          autoFocus={autoFocus}
          onChange={e => handleChange(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') { e.preventDefault(); handleSave(); }
            if (e.key === 'Escape') handleChange('');
          }}
          // 목록 항목 안에 놓이는 경우가 있어 상위 링크로 클릭이 새지 않게 막는다
          onClick={e => { e.preventDefault(); e.stopPropagation(); }}
          placeholder="쪽"
          aria-label={`${book.title} 오늘 읽은 쪽수`}
          className="!w-[4.5rem] !px-2 text-center"
        />
      </div>
      <button
        type="button"
        onClick={e => { e.preventDefault(); e.stopPropagation(); handleSave(); }}
        disabled={!valid || saving}
        // 손가락으로 누르는 자리라 아이콘보다 여백을 넉넉히 잡는다
        className="p-2.5 -m-0.5 text-ink-500 dark:text-ink-400 hover:text-ink-900 dark:hover:text-ink-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        aria-label="기록"
        title="오늘 읽은 쪽수 기록 (Enter)"
      >
        <Check size={16} strokeWidth={1.5} />
      </button>
    </div>
  );
};

export default ProgressQuickInput;
